import { useState, useEffect, useCallback, useRef, useId } from "react";
import type { Locale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import {
  type Tab,
  type SizeChartData,
  SIZES_XS_XXL,
  FOOTWEAR_SIZES,
  TOPS_DATA,
  BOTTOMS_DATA,
  FOOTWEAR_DATA,
  detectChartTab,
  parseSizeChartData,
} from "~/lib/sizeChart/defaults";

type Unit = "cm" | "in";

interface SizeGuideModalProps {
  open: boolean;
  onClose: () => void;
  locale: Locale;
  /** Product type string to auto-select the right tab */
  productType?: string | null;
  /** Per-product structured size chart. Validated; null falls back to defaults. */
  customChart?: SizeChartData | unknown | null;
  /** Per-product chart image URL. Renders below/instead of the default table when set. */
  customImage?: string | null;
  /** Open directly into a view — "quiz" lands on the Find-my-size quiz
      (used by the PDP's inline "Find my size" link). Default "chart". */
  initialMode?: "chart" | "quiz";
}

function cmToInch(cm: number): string {
  return (cm / 2.54).toFixed(1);
}

// ─── Translation helper keys ─── //

const TAB_LABELS: Record<Tab, Record<Locale, string>> = {
  tops:     { vi: "Áo", en: "Tops" },
  bottoms:  { vi: "Quần", en: "Bottoms" },
  footwear: { vi: "Giày dép", en: "Footwear" },
};

const MEASUREMENT_LABELS: Record<string, Record<Locale, string>> = {
  chest:      { vi: "Vòng ngực", en: "Chest" },
  length:     { vi: "Chiều dài", en: "Length" },
  shoulder:   { vi: "Vai", en: "Shoulder" },
  sleeve:     { vi: "Tay áo", en: "Sleeve" },
  waist:      { vi: "Vòng eo", en: "Waist" },
  hip:        { vi: "Vòng mông", en: "Hip" },
  footLength: { vi: "Chiều dài chân", en: "Foot Length" },
  eu:         { vi: "EU", en: "EU" },
  us:         { vi: "US", en: "US" },
};

// ─── Component ─── //

// ─── Find-my-size quiz (added 2026-04-30 per merchant request) ───
// Match algorithm: for each size, count how many of the customer's
// (chest, waist, hip) measurements fall within that size's range.
// Pick the size with the most hits; tie-break to the smaller size
// (easier to size up than down at returns).

interface QuizResult {
  size: string;
  note?: "underXS" | "overXXL" | "between";
}

function recommendSize(
  category: Tab,
  meas: { chest?: number; waist?: number; hip?: number },
  // When the modal renders a custom (per-product) chart, route the
  // recommendation through that product's actual measurements instead
  // of the generic defaults. Same algorithm — just sourced numbers.
  customMeasurements?: Record<string, number[]> | null,
  customSizes?: string[] | null,
): QuizResult {
  const sizes = customSizes && customSizes.length > 0 ? customSizes : SIZES_XS_XXL;
  // Build a per-size {chest, waist, hip} range table from existing
  // chart data. Each size has a +/-2cm "tolerance" around the listed
  // value so customers near a boundary still get a hit.
  const toleranceCm = 2;
  const dataByKey: Record<string, number[]> = customMeasurements
    ? customMeasurements
    : category === "bottoms"
      ? BOTTOMS_DATA
      : category === "tops"
        ? TOPS_DATA
        : TOPS_DATA;

  const hits = sizes.map((size, i) => {
    let count = 0;
    if (dataByKey.chest && meas.chest !== undefined) {
      const c = dataByKey.chest[i];
      if (c !== undefined && Math.abs(meas.chest - c) <= toleranceCm) count++;
    }
    if (dataByKey.waist && meas.waist !== undefined) {
      const w = dataByKey.waist[i];
      if (w !== undefined && Math.abs(meas.waist - w) <= toleranceCm) count++;
    }
    if (dataByKey.hip && meas.hip !== undefined) {
      const h = dataByKey.hip[i];
      if (h !== undefined && Math.abs(meas.hip - h) <= toleranceCm) count++;
    }
    return { size, count };
  });

  const max = Math.max(...hits.map((h) => h.count));
  if (max > 0) {
    return { size: hits.find((h) => h.count === max)!.size };
  }

  // No range matched — fall back to closest by waist (most universal anchor).
  const anchor = meas.waist ?? meas.chest ?? meas.hip;
  // Helper: middle size of whatever sizes array we're operating on.
  // Old code hardcoded "M" / "XS" / "XXL"; per-product charts may use
  // different size strings (e.g. ["P","S","M","L"]) so we resolve
  // first/last/middle from the actual sizes array passed in.
  const middle = sizes[Math.floor(sizes.length / 2)] || "M";
  const smallest = sizes[0] || "XS";
  const largest = sizes[sizes.length - 1] || "XXL";
  if (anchor === undefined) return { size: middle, note: "between" };
  const series = dataByKey.waist || dataByKey.chest || dataByKey.hip || [];
  if (series.length === 0) return { size: middle, note: "between" };
  if (anchor < series[0] - toleranceCm) return { size: smallest, note: "underXS" };
  if (anchor > series[series.length - 1] + toleranceCm)
    return { size: largest, note: "overXXL" };
  // Pick closest
  let best = sizes[0];
  let bestDiff = Infinity;
  series.forEach((v, i) => {
    const d = Math.abs(anchor - v);
    if (d < bestDiff) {
      bestDiff = d;
      best = sizes[i];
    }
  });
  return { size: best, note: "between" };
}

export function SizeGuideModal({
  open,
  onClose,
  locale,
  productType,
  customChart: rawCustomChart,
  customImage,
  initialMode = "chart",
}: SizeGuideModalProps) {
  // Run the customChart through the strict validator. If it doesn't
  // pass (corrupt JSON, mismatched array lengths, missing keys) we
  // fall through to the default chart for the matching tab. The
  // server-side write path is also strictly validated, so we should
  // rarely get a malformed value here — this is just defence in depth.
  const customChart = parseSizeChartData(rawCustomChart);

  // If a custom chart is present, lock the active tab to its category.
  // Otherwise auto-detect from productType (existing behaviour).
  const initialTab: Tab = customChart
    ? customChart.category
    : detectChartTab(productType);

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [unit, setUnit] = useState<Unit>("cm");
  // Find-my-size quiz state
  const [mode, setMode] = useState<"chart" | "quiz">("chart");
  const [qHeight, setQHeight] = useState("");
  const [qWeight, setQWeight] = useState("");
  const [qChest, setQChest] = useState("");
  const [qWaist, setQWaist] = useState("");
  const [qHip, setQHip] = useState("");
  const [qSubmitted, setQSubmitted] = useState(false);
  // T2.1: focus trap + labelled dialog
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstQuizInputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  useFocusTrap(dialogRef, open, onClose);

  // Reset tab when product type or custom chart changes
  useEffect(() => {
    setActiveTab(customChart ? customChart.category : detectChartTab(productType));
  }, [productType, customChart]);

  // Reset quiz state on close so reopening shows a fresh form; on open,
  // land on the caller-requested view (e.g. straight into the quiz).
  useEffect(() => {
    if (!open) {
      setMode("chart");
      setQSubmitted(false);
    } else {
      setMode(initialMode);
    }
  }, [open, initialMode]);

  // Bug fix (2026-05-07): when the user clicked "Find my size" the chart
  // CTA button unmounted and the quiz overlay took its place. The previously
  // focused element vanished, dropping document.activeElement to <body>.
  // The first click on a measurement input was then absorbed by the browser
  // as a "scroll-into-view + establish focus context" pass — only the
  // SECOND click actually focused the field and let the user type.
  // Auto-focusing the first input the moment quiz mode mounts gives the
  // browser a clean focus target up front, so the first click immediately
  // begins typing. (Also a small UX win: customer doesn't have to find the
  // first field themselves.)
  useEffect(() => {
    if (mode === "quiz" && !qSubmitted) {
      // requestAnimationFrame waits for the quiz overlay to actually paint
      // — focusing before paint is silently dropped on some browsers.
      const id = requestAnimationFrame(() => {
        firstQuizInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [mode, qSubmitted]);

  // Body scroll lock — Escape is handled by useFocusTrap
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const tabs: Tab[] = ["tops", "bottoms", "footwear"];
  const isFootwear = activeTab === "footwear";

  // Render priority for the chart table:
  //   1. Custom structured chart matching the active tab → render its
  //      sizes + measurements directly (best UX — keeps unit toggle
  //      and the find-my-size quiz still works).
  //   2. Default matrix for the active tab (TOPS_DATA / BOTTOMS_DATA /
  //      FOOTWEAR_DATA) — original behaviour.
  // The customImage (if any) is rendered separately below the table —
  // it doesn't replace the table, it augments it. Merchants who only
  // upload an image and don't fill in measurements still get the
  // default-matrix table for their category.
  const useCustom = customChart && customChart.category === activeTab;
  const sizes: string[] = useCustom
    ? customChart.sizes
    : isFootwear
      ? FOOTWEAR_SIZES
      : SIZES_XS_XXL;
  const rawData: Record<string, number[]> = useCustom
    ? customChart.measurements
    : activeTab === "tops"
      ? TOPS_DATA
      : activeTab === "bottoms"
        ? BOTTOMS_DATA
        : FOOTWEAR_DATA;

  // Defensive: cap each measurement row at sizes.length so a corrupt
  // row never overflows the <table>. parseSizeChartData already rejects
  // mismatched lengths so this is purely belt-and-braces.
  const data: Record<string, number[]> = Object.fromEntries(
    Object.entries(rawData).map(([k, v]) => [k, v.slice(0, sizes.length)]),
  );

  function formatVal(key: string, val: number): string {
    // EU/US sizes are unitless
    if (key === "eu" || key === "us") return String(val);
    if (unit === "in") return cmToInch(val);
    return String(val);
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 animate-fadeIn"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="bg-white w-[95vw] max-w-2xl mx-4 relative animate-slideUp max-h-[90vh] max-h-[90dvh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* ─── Header ─── */}
        <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4">
          <h2 id={titleId} className="text-[13px] font-medium uppercase tracking-[0.2em] text-luxury-charcoal">
            {t("sizeGuideTitle", locale)}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-luxury-stone hover:text-luxury-charcoal transition-colors"
            aria-label={t("close", locale)}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ─── Tabs ─── */}
        <div className="flex border-b border-luxury-sand/60 px-6 sm:px-8">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 mr-6 text-[12px] uppercase tracking-widest font-medium transition-colors border-b-2 ${
                activeTab === tab
                  ? "text-luxury-charcoal border-luxury-charcoal"
                  : "text-luxury-stone border-transparent hover:text-luxury-charcoal"
              }`}
            >
              {TAB_LABELS[tab][locale]}
            </button>
          ))}
        </div>

        {/* ─── Find My Size CTA (moved to TOP 2026-05-14 per merchant feedback) ───
            Was previously rendered AFTER the "How to Measure" block at
            the bottom of the modal, which meant customers had to scroll
            past the entire size table + 4-step measuring guide before
            seeing the personalized-recommendation button. Most customers
            who open the modal want a quick "what size am I?" answer first
            and only consult the table second — surfacing the quiz button
            above the fold is the higher-converting placement.

            Still gated to non-footwear chart mode (the quiz only has
            chest/waist/hip inputs which don't map to shoe sizing). */}
        {mode === "chart" && !isFootwear && (
          <div className="px-6 sm:px-8 pt-4">
            <button
              type="button"
              onClick={() => { setMode("quiz"); setQSubmitted(false); }}
              className="w-full h-12 bg-luxury-charcoal text-white text-[12px] uppercase tracking-widest hover:bg-black transition-colors"
            >
              {locale === "vi" ? "Tìm size của tôi →" : "Find my size →"}
            </button>
          </div>
        )}

        {/* ─── Unit Toggle ─── */}
        <div className="flex justify-end px-6 sm:px-8 pt-4">
          <div className="inline-flex border border-luxury-sand text-[11px] uppercase tracking-wider">
            <button
              onClick={() => setUnit("cm")}
              className={`px-3 py-1.5 transition-colors ${
                unit === "cm"
                  ? "bg-luxury-charcoal text-white"
                  : "text-luxury-stone hover:text-luxury-charcoal"
              }`}
            >
              CM
            </button>
            <button
              onClick={() => setUnit("in")}
              className={`px-3 py-1.5 transition-colors ${
                unit === "in"
                  ? "bg-luxury-charcoal text-white"
                  : "text-luxury-stone hover:text-luxury-charcoal"
              }`}
            >
              IN
            </button>
          </div>
        </div>

        {/* ─── Size Table ─── */}
        <div className="px-6 sm:px-8 pt-4 pb-2 overflow-x-auto">
          <table className="w-full text-[13px] text-luxury-charcoal">
            <thead>
              <tr className="border-b border-luxury-sand">
                <th className="text-left py-2.5 pr-4 text-[11px] uppercase tracking-widest text-luxury-stone font-medium">
                  {t("size", locale)}
                </th>
                {sizes.map((s) => (
                  <th
                    key={s}
                    className="text-center py-2.5 px-2 sm:px-3 text-[11px] uppercase tracking-widest text-luxury-stone font-medium"
                  >
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data).map(([key, values], idx) => (
                <tr
                  key={key}
                  className={idx < Object.keys(data).length - 1 ? "border-b border-luxury-sand/50" : ""}
                >
                  <td className="py-2.5 pr-4 font-medium whitespace-nowrap">
                    {MEASUREMENT_LABELS[key]?.[locale] || key}
                    {key !== "eu" && key !== "us" && (
                      <span className="text-luxury-stone font-normal ml-1">
                        ({unit})
                      </span>
                    )}
                  </td>
                  {values.map((val, i) => (
                    <td key={i} className="text-center py-2.5 px-2 sm:px-3">
                      {formatVal(key, val)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ─── Per-product chart image (optional) ─── */}
        {/* Some merchants upload a polished marketing-style chart image
            in addition to (or instead of customising) the structured
            measurements. Render it below the table so a customer who
            prefers the visual reference still sees it. */}
        {customImage && (
          <div className="px-6 sm:px-8 pb-2">
            <div className="border-t border-luxury-sand/40 pt-4">
              <h3 className="text-[12px] font-medium uppercase tracking-widest text-luxury-charcoal mb-3">
                {locale === "vi" ? "Bảng size sản phẩm" : "Product size chart"}
              </h3>
              <img
                src={customImage}
                alt={locale === "vi" ? "Bảng size" : "Size chart"}
                className="w-full h-auto rounded-sm"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* ─── How to Measure ─── */}
        <div className="px-6 sm:px-8 pt-4 pb-6 sm:pb-8">
          <div className="border-t border-luxury-sand/40 pt-4">
            <h3 className="text-[12px] font-medium uppercase tracking-widest text-luxury-charcoal mb-3">
              {t("howToMeasure", locale)}
            </h3>
            <ul className="space-y-2 text-[12px] text-luxury-stone leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-luxury-charcoal">1.</span>
                <span>{t("measureChest", locale)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-luxury-charcoal">2.</span>
                <span>{t("measureWaist", locale)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-luxury-charcoal">3.</span>
                <span>{t("measureHip", locale)}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-luxury-charcoal">4.</span>
                <span>{t("measureLength", locale)}</span>
              </li>
            </ul>
          </div>

          <p className="text-[11px] text-luxury-stone mt-4">
            {t("sizeChartNote", locale)}
          </p>
          {/* Find My Size CTA moved to the TOP of the modal (above the
              unit toggle) on 2026-05-14 per merchant feedback. No button
              rendered here anymore. */}
        </div>

        {/* ─── Find My Size quiz overlay ─── */}
        {mode === "quiz" && (
          <div className="absolute inset-0 bg-white flex flex-col">
            <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-luxury-sand/60">
              <button
                type="button"
                onClick={() => setMode("chart")}
                className="text-[11px] uppercase tracking-widest text-luxury-stone hover:text-luxury-charcoal flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {locale === "vi" ? "Quay lại bảng size" : "Back to size chart"}
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-luxury-stone hover:text-luxury-charcoal transition-colors"
                aria-label={t("close", locale)}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto px-6 sm:px-8 py-6">
              {!qSubmitted && (
                <>
                  <h3 className="text-[15px] font-medium text-luxury-charcoal mb-2">
                    {locale === "vi" ? "Tìm size của tôi" : "Find my size"}
                  </h3>
                  <p className="text-[12px] text-luxury-stone leading-relaxed mb-5">
                    {locale === "vi"
                      ? "Trả lời 5 câu hỏi nhanh và chúng tôi sẽ gợi ý size phù hợp nhất với số đo của bạn."
                      : "Answer 5 quick questions and we'll suggest the size that best fits your measurements."}
                  </p>
                  <form
                    onSubmit={(e) => { e.preventDefault(); setQSubmitted(true); }}
                    className="space-y-4"
                  >
                    {[
                      { label: locale === "vi" ? "Chiều cao (cm)" : "Height (cm)", value: qHeight, set: setQHeight, min: 130, max: 200, placeholder: "168" },
                      { label: locale === "vi" ? "Cân nặng (kg)" : "Weight (kg)", value: qWeight, set: setQWeight, min: 30, max: 150, placeholder: "55" },
                      { label: locale === "vi" ? "Vòng ngực (cm)" : "Bust (cm)", value: qChest, set: setQChest, min: 60, max: 130, placeholder: "84" },
                      { label: locale === "vi" ? "Vòng eo (cm)" : "Waist (cm)", value: qWaist, set: setQWaist, min: 50, max: 120, placeholder: "66" },
                      { label: locale === "vi" ? "Vòng hông (cm)" : "Hip (cm)", value: qHip, set: setQHip, min: 60, max: 140, placeholder: "90" },
                    ].map((field, idx) => (
                      <div key={idx}>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-luxury-stone mb-1.5">
                          {field.label}
                        </label>
                        <input
                          // Ref on the first input so the auto-focus
                          // effect above can target it when quiz mode
                          // opens — fixes the "click twice to type" bug.
                          ref={idx === 0 ? firstQuizInputRef : undefined}
                          type="number"
                          inputMode="decimal"
                          step="0.5"
                          min={field.min}
                          max={field.max}
                          value={field.value}
                          onChange={(e) => field.set(e.target.value)}
                          placeholder={field.placeholder}
                          required
                          className="w-full h-11 px-3 text-sm text-luxury-charcoal border border-luxury-sand focus:border-luxury-charcoal focus:outline-none"
                        />
                      </div>
                    ))}
                    <button
                      type="submit"
                      className="w-full h-12 bg-luxury-charcoal text-white text-[12px] uppercase tracking-widest hover:bg-black transition-colors"
                    >
                      {locale === "vi" ? "Gợi ý size" : "Suggest my size"}
                    </button>
                  </form>
                </>
              )}

              {qSubmitted && (() => {
                const meas = {
                  chest: Number.parseFloat(qChest) || undefined,
                  waist: Number.parseFloat(qWaist) || undefined,
                  hip: Number.parseFloat(qHip) || undefined,
                };
                // Thread the per-product chart through so quiz suggestions
                // are based on the actual product's measurements, not the
                // generic Tops/Bottoms defaults. Falls back when no custom
                // chart is provided.
                const result = recommendSize(
                  activeTab,
                  meas,
                  useCustom ? customChart!.measurements : null,
                  useCustom ? customChart!.sizes : null,
                );
                return (
                  <div className="space-y-5">
                    <div className="border border-luxury-charcoal p-6 text-center">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-luxury-stone mb-3">
                        {locale === "vi" ? "Gợi ý của chúng tôi" : "Our recommendation"}
                      </p>
                      <p className="font-display text-5xl font-light text-luxury-charcoal tracking-normal mb-3">
                        {result.size}
                      </p>
                      <p className="text-[13px] text-luxury-stone leading-relaxed max-w-sm mx-auto">
                        {locale === "vi"
                          ? `Dựa trên số đo của bạn, chúng tôi gợi ý size ${result.size}. Nếu bạn thích form rộng hơn, chọn size lớn hơn 1 size.`
                          : `Based on your measurements, we suggest size ${result.size}. If you prefer a looser fit, size up by one.`}
                      </p>
                    </div>
                    {result.note === "underXS" && (
                      <p className="text-[12px] text-luxury-stone">
                        {locale === "vi"
                          ? "Số đo của bạn nhỏ hơn size XS. Bạn vẫn có thể thử XS, có thể hơi rộng."
                          : "Your measurements are below our XS range. XS may run slightly loose."}
                      </p>
                    )}
                    {result.note === "overXXL" && (
                      <p className="text-[12px] text-luxury-stone">
                        {locale === "vi"
                          ? "Số đo của bạn lớn hơn size XXL. Hãy email hello@diolo.com để được tư vấn."
                          : "Your measurements are above our XXL range. Email hello@diolo.com for guidance."}
                      </p>
                    )}
                    {result.note === "between" && (
                      <p className="text-[12px] text-luxury-stone">
                        {locale === "vi"
                          ? "Số đo của bạn nằm giữa các size. Hãy thử size gợi ý làm điểm bắt đầu."
                          : "Your measurements sit between sizes. Use the suggested size as a starting point."}
                      </p>
                    )}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setQSubmitted(false)}
                        className="flex-1 h-11 border border-luxury-charcoal text-[12px] uppercase tracking-widest text-luxury-charcoal hover:bg-luxury-charcoal hover:text-white transition-colors"
                      >
                        {locale === "vi" ? "Nhập lại" : "Start over"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setMode("chart")}
                        className="flex-1 h-11 bg-luxury-charcoal text-white text-[12px] uppercase tracking-widest hover:bg-black transition-colors"
                      >
                        {locale === "vi" ? "Xem bảng size" : "View size chart"}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
